import { useLayoutEffect, useRef, useState } from 'react';

/**
 * 한 줄에 들어가는 썸네일 칸 수 — 컨테이너(ref) 너비 기준.
 *
 * 화면기획서(D 작업보고확인 v1.8): 사진 썸네일(64×64)은 카드 너비에 들어가는 개수만큼 보이고, 넘치면 마지막 칸을
 * "+N 더보기" 타일로 쓴다. 1열↔2열 전환·창 크기·사이드 패널 변화로 카드 너비가 바뀌면 개수도 다시 계산해야 하므로
 * ResizeObserver 로 컨테이너 너비를 따라간다.
 *
 * 첫 측정 전에는 Infinity(전부 렌더)다 — useLayoutEffect 가 페인트 전에 측정하므로 화면에서 깜빡이지 않는다.
 * 호출부 계산: {@code collapsed = total > fit} 이면 {@code fit - 1}장 + 타일(+{total - (fit - 1)})을 그린다(fit 은 최소 1).
 *
 * @param slot 썸네일 한 칸 너비(px) — 호출부 썸네일 크기와 같아야 한다(`h-10 w-10` = spacing-10 = 64px)
 * @param gap 칸 사이 간격(px) — 호출부 gap 과 같아야 한다(`gap-sm` = 8px)
 * @param enabled 썸네일 영역이 실제로 렌더될 때만 true(없는 요소는 측정하지 않는다)
 */
export function useFitThumbCount<T extends HTMLElement>(slot = 64, gap = 8, enabled = true) {
  const ref = useRef<T>(null);
  const [fit, setFit] = useState(Infinity);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!enabled || !el) return;
    const measure = () => {
      const width = el.clientWidth;
      if (width > 0) setFit(Math.max(1, Math.floor((width + gap) / (slot + gap))));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [slot, gap, enabled]);

  return { ref, fit };
}
